import Image from 'next/image'
import { Intercom } from 'react-live-chat-loader'
import { CTASection } from '../components/marketing/CTASection'
import { Header } from '../components/marketing/Header'
import Meta from '../components/Meta'
import { SignupButton } from '../designsystem/SignupButton'
import { Marketing, SubHeadline } from '../designsystem/Text'

export default function Features() {
  return (
    <>
      <div className='bg-gray-50'>
        <Header />
        <Intercom color='#608BD8' />
        <Meta
          title='Features – GraphCDN'
          description='Scale, inspect & protect your GraphQL API'
          // imageSrc='https://graphcdn.io/tim-and-max.jpg'
          // imageHeight={2785}
          // imageWidth={5570}
        />
        <div className='max-w-4xl px-6 pt-12 pb-24 mx-auto space-y-8 text-center sm:px-6 sm:pt-24'>
          <Marketing.H1>
            Scale, inspect &amp; protect your GraphQL API
          </Marketing.H1>
        </div>
        <div className='max-w-full overflow-hidden'>
          <div
            className='flex justify-center'
            style={{
              minHeight: 876,
              minWidth: 'calc(300vw)',
              maxWidth: 3057,
              margin: '0 calc(-100vw)',
            }}
          >
            <Image
              src='/features.svg'
              alt=''
              width={3057}
              height={876}
              priority
              className='w-screen h-auto'
            />
          </div>
        </div>
        <main className='px-6 pt-12 pb-40 mx-auto space-y-56 text-center sm:px-6 max-w-7xl'>
          <div>
            <SubHeadline className='mb-2 tracking-wider opacity-100 text-bg-blue'>
              Caching
            </SubHeadline>
            <Marketing.H2 className='max-w-4xl mx-auto'>
              Reduce load for your backend &amp; get to maximum speed.
            </Marketing.H2>
            <Cols>
              <Feature
                icon={Control}
                title='Fine-grained control'
                content='Cache query results that contains a User for 900s and ones that contain an APIToken for 0s? – No problem.'
              />
              <Feature
                icon={Purging}
                title='Powerful cache purging'
                content='Purge specific objects, like "The User #5", and invalidate all cached query results that contain them.'
              />
              <Feature
                icon={PerUser}
                title='Scope cached data per-user'
                content='Configure how requests are authenticated and which fields are private to securely cache user-specific data. '
              />
            </Cols>
          </div>

          <div>
            <SubHeadline className='mb-2 tracking-wider opacity-100 text-bg-blue'>
              Analytics &amp; error tracking
            </SubHeadline>
            <Marketing.H2>
              Get insights into your GraphQL API and debug issues faster
            </Marketing.H2>
            <Cols>
              <Feature
                icon={Analytics}
                title='Understand your API’s traffic'
                content='Monitor and analyse queries and mutations. Get insights on cache hit rates and request origins up to 30 days in the past.'
              />
              <Feature
                icon={Performance}
                title='Stay on top of your performance'
                content='Swiftly debug performance problems by checking your origin’s p95 response times to specific queries and mutations.'
              />
              <Feature
                icon={Alerts}
                title='Identify errors &amp; get alerted'
                content='Automatically receive email alerts the moment HTTP or GraphQL error rates spike.'
              />
            </Cols>
          </div>

          <div>
            <SubHeadline className='mb-2 tracking-wider opacity-100 text-bg-blue'>
              Security
            </SubHeadline>
            <Marketing.H2>
              Protect your GraphQL API from malicious actors
            </Marketing.H2>
            <Cols>
              <Feature
                icon={Depth}
                title='Query depth limiting'
                content='GraphCDN analyses incoming queries and automatically prevents server or database overloads by blocking deeply nested queries at the edge.'
              />
              <Feature
                icon={DDOS}
                title='DDOS attack protection'
                content='Layer 3 and 4 attacks like ping floods, ICMP floods, UDP abuse as well as distributed reflection and amplification attacks are filtered out by GraphCDN.'
              />
              <Feature
                icon={Anomaly}
                title='Anomaly detection'
                content='Get notified if any unusual traffic against your API occurs.'
              />
            </Cols>
          </div>

          <div>
            <Marketing.H2>That's not all!</Marketing.H2>
            <Cols>
              <Feature
                icon={null}
                title='Works with all GraphQL backends'
                content='Your servers can be written in any language you want. GraphCDN is an API gateway that sits in front of your infrastructure and proxies through any uncached requests. It even supports POST requests!'
              />
              <Feature
                icon={null}
                title='Automatic mutation invalidation'
                content='GraphCDN keeps track of the mutations sent to your GraphQL API and automatically invalidates any cached query results that contain the changed data. (e.g. editUser(id: 5) will invalidate all query results that contain that User)'
              />
            </Cols>
          </div>
        </main>
      </div>
      <CTASection />
    </>
  )
}

function Feature({ title, content, icon: Icon }) {
  return (
    <div className='flex-1 flex-shrink-0 space-y-6 text-left'>
      {Icon && <Icon />}
      <div className='space-y-2'>
        <Marketing.H3>{title}</Marketing.H3>
        <p className='md:pt-4 gotham text-gray opacity-80 md:text-2xl'>
          {content}
        </p>
      </div>
    </div>
  )
}

function Cols(props) {
  return (
    <div
      className='w-full mt-20 space-y-8 md:space-y-0 md:space-x-8 md:flex md:justify-between md:mt-28'
      {...props}
    ></div>
  )
}

function PerUser(props) {
  return (
    <svg viewBox='0 0 90 92' fill='none' className='w-auto h-16' {...props}>
      <path
        d='M68.348 90.647l2.19-.832C80.959 85.853 90 75.853 90 64.705V51.248a4.137 4.137 0 00-2.488-3.795 62.654 62.654 0 00-21.371-3.962 61.493 61.493 0 00-21.197 3.962 4.135 4.135 0 00-2.488 3.795v13.443c0 11.15 8.867 21.141 19.287 25.111l2.191.832c1.42.547 2.991.551 4.414.012z'
        fill='#608BD8'
      />
      <path
        opacity={0.5}
        d='M39.084 43.722c.493-.56.369-1.465-.328-1.731-4.898-1.87-12.088-2.591-15.765-1.95A27.767 27.767 0 00.02 66.317a1.006 1.006 0 001.016 1.037H35.23c.61 0 1.086-.524 1.053-1.132a29.041 29.041 0 01-.04-1.53V51.25a11.385 11.385 0 012.841-7.527z'
        fill='#608BD8'
      />
      <path
        d='M66.23 51.423v23.772M78.116 63.31H54.344'
        stroke='#fff'
        strokeWidth={3.10654}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        opacity={0.5}
        d='M27.735 33.677c9.3 0 16.838-7.539 16.838-16.838C44.573 7.538 37.034 0 27.735 0c-9.3 0-16.839 7.539-16.839 16.838 0 9.3 7.539 16.839 16.838 16.839z'
        fill='#608BD8'
      />
    </svg>
  )
}

function Control(props) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 124 80'
      className='w-auto h-16'
      {...props}
    >
      <rect width='16' height='40' x='34' fill='#608BD8' rx='4'></rect>
      <rect
        width='26'
        height='8'
        y='16'
        fill='#608BD8'
        opacity='0.4'
        rx='2'
      ></rect>
      <rect
        width='66'
        height='8'
        x='58'
        y='16'
        fill='#608BD8'
        opacity='0.4'
        rx='2'
      ></rect>
      <rect
        width='16'
        height='40'
        x='90'
        y='80'
        fill='#608BD8'
        rx='4'
        transform='rotate(-180 90 80)'
      ></rect>
      <rect
        width='26'
        height='8'
        x='124'
        y='64'
        fill='#608BD8'
        opacity='0.4'
        rx='2'
        transform='rotate(-180 124 64)'
      ></rect>
      <rect
        width='66'
        height='8'
        x='66'
        y='64'
        fill='#608BD8'
        opacity='0.4'
        rx='2'
        transform='rotate(-180 66 64)'
      ></rect>
    </svg>
  )
}

function Purging(props) {
  return (
    <svg viewBox='0 0 192 58' fill='none' className='w-40 h-16' {...props}>
      <path
        d='M53.731 29.436C53.731 15.703 42.6 4.57 28.866 4.57 15.133 4.57 4 15.703 4 29.436S15.133 54.3 28.866 54.3 53.73 43.17 53.73 29.436z'
        fill='#608BD8'
        stroke='#608BD8'
        strokeWidth={6.21644}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M187.981 28.866C187.981 15.133 176.848 4 163.115 4S138.25 15.133 138.25 28.866s11.132 24.865 24.865 24.865c13.733 0 24.866-11.132 24.866-24.865z'
        stroke='#608BD8'
        strokeWidth={6.21644}
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeDasharray='0.02 14.51'
      />
      <path
        opacity={0.4}
        d='M90.978 42.672l12.433-12.433-12.433-12.433'
        stroke='#608BD8'
        strokeWidth={4.1443}
      />
    </svg>
  )
}

function Analytics(props) {
  return (
    <svg viewBox='0 0 112 80' fill='none' className='w-24 h-16' {...props}>
      <rect
        opacity={0.8}
        y={17.0667}
        width={16}
        height={62.9333}
        rx={4}
        fill='#608BD8'
      />
      <rect x={24} width={16} height={80} rx={4} fill='#608BD8' />
      <rect
        opacity={0.5}
        x={72}
        y={23.4667}
        width={16}
        height={56.5333}
        rx={4}
        fill='#608BD8'
      />
      <rect
        opacity={0.8}
        x={48}
        y={7.46667}
        width={16}
        height={72.5333}
        rx={4}
        fill='#608BD8'
      />
      <rect
        opacity={0.3}
        x={96}
        y={38.4}
        width={16}
        height={41.6}
        rx={4}
        fill='#608BD8'
      />
    </svg>
  )
}

function Performance(props) {
  return (
    <svg className='w-20 h-16' viewBox='0 0 104 80' fill='none' {...props}>
      <circle cx={89} cy={40} r={15} fill='#608BD8' />
      <path
        d='M87.832 42.672L86.92 32.04v-.84h3.408v.84l-.888 10.632h-1.608zM87.04 48v-3.312h3.192V48H87.04z'
        fill='#F8FAFC'
      />
      <rect width={40} height={80} rx={4} fill='#608BD8' />
      <rect
        opacity={0.25}
        x={52}
        y={37}
        width={40}
        height={43}
        rx={4}
        fill='#608BD8'
      />
    </svg>
  )
}

function Alerts(props) {
  return (
    <svg className='w-32 h-16' viewBox='0 0 168 76' fill='none' {...props}>
      <path
        d='M0 4a4 4 0 014-4h160a4 4 0 014 4v45a4 4 0 01-4 4H52.263a8 8 0 00-5.595 2.283L28.049 73.505c-.95.93-2.549.257-2.549-1.072V56a3 3 0 00-3-3H4a4 4 0 01-4-4V4z'
        fill='#608BD8'
      />
      <circle opacity={0.3} cx={27} cy={26} r={15} fill='#F8FAFC' />
      <path
        d='M25.832 28.672L24.92 18.04v-.84h3.408v.84l-.888 10.632h-1.608zM25.04 34v-3.312h3.192V34H25.04z'
        fill='#F8FAFC'
      />
      <rect
        opacity={0.3}
        x={60}
        y={21}
        width={91}
        height={11}
        rx={2}
        fill='#F8FAFC'
      />
    </svg>
  )
}

function Depth(props) {
  return (
    <svg className='w-16 h-16' viewBox='0 0 92 80' fill='none' {...props}>
      <rect width={12} height={80} rx={4} fill='#608BD8' />
      <rect x={20} width={12} height={80} rx={4} fill='#608BD8' />
      <rect opacity={0.3} x={60} width={12} height={80} rx={4} fill='#608BD8' />
      <rect x={40} width={12} height={80} rx={4} fill='#608BD8' />
      <rect opacity={0.3} x={80} width={12} height={80} rx={4} fill='#608BD8' />
    </svg>
  )
}

function DDOS(props) {
  return (
    <svg className='w-auto h-16' viewBox='0 0 86 86' fill='none' {...props}>
      <g stroke='#608BD8' strokeLinecap='round' strokeLinejoin='round'>
        <path
          d='M25 27c0 13.5 1.89 30.036 18 36 16.11-5.964 18-22.5 18-36H25z'
          fill='#608BD8'
          strokeWidth={4}
        />
        <g opacity={0.5} strokeWidth={4.5}>
          <path d='M43 1.792v3.583M58.769 4.927l-1.372 3.31M72.139 13.86l-2.533 2.534M81.074 27.23l-3.311 1.372M84.208 43h-3.583M81.074 58.77l-3.311-1.373M72.139 72.14l-2.533-2.534M58.769 81.073l-1.372-3.311M43 84.208v-3.583M27.229 81.073l1.372-3.311M13.861 72.14l2.534-2.534M4.927 58.77l3.31-1.373M1.792 43h3.583M4.927 27.23l3.31 1.372M13.861 13.86l2.534 2.534M27.229 4.927l1.372 3.31' />
        </g>
      </g>
    </svg>
  )
}

function Anomaly(props) {
  return (
    <svg className='w-auto h-16' viewBox='0 0 176 83' fill='none' {...props}>
      <circle opacity={0.2} cx={6} cy={77} r={6} fill='#608BD8' />
      <circle opacity={0.4} cx={29} cy={74} r={6} fill='#608BD8' />
      <circle opacity={0.3} cx={47} cy={61} r={6} fill='#608BD8' />
      <circle opacity={0.4} cx={77} cy={67} r={6} fill='#608BD8' />
      <circle opacity={0.2} cx={105} cy={54} r={6} fill='#608BD8' />
      <circle opacity={0.3} cx={128} cy={64} r={6} fill='#608BD8' />
      <circle opacity={0.4} cx={170} cy={41} r={6} fill='#608BD8' />
      <circle
        opacity={0.3}
        cx={64.5}
        cy={14.5}
        r={13.5}
        stroke='#608BD8'
        strokeWidth={2}
      />
      <circle cx={64.5} cy={14.5} r={6.5} fill='#608BD8' />
    </svg>
  )
}
